import * as THREE from 'three';

/**
 * Evenly distributes `count` points on a sphere of radius `r`
 * using the Fibonacci lattice.
 */
export function fibonacciPoints(count: number, r: number): THREE.Vector3[] {
  const pts: THREE.Vector3[] = [];
  const off = 2 / count;
  const inc = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < count; i++) {
    const y  = i * off - 1 + off / 2;
    const ρ  = Math.sqrt(1 - y * y);
    const θ  = i * inc;
    pts.push(new THREE.Vector3(Math.cos(θ) * ρ * r, y * r, Math.sin(θ) * ρ * r));
  }
  return pts;
}

/**
 * Generates `count` points along a spiral (“corkscrew”) that winds
 * `turns` times from south to north pole on a sphere of radius `r`.
 *
 * `bias` squeezes the latitude toward the equator (0 = uniform, 1 = fully
 * equatorial).  Oversampling smooths the final resampling step so the
 * points end up roughly equidistant along the curve.
 */
export function corkscrewPoints(
  count: number,
  r: number,
  turns = 4,
  bias  = 0,
  oversampleFactor = 8,
): THREE.Vector3[] {
  if (count <= 1) return [new THREE.Vector3(0, r, 0)];

  const denseCnt = Math.max(2, count * oversampleFactor);
  const dense: THREE.Vector3[] = [];
  for (let j = 0; j < denseCnt; j++) {
    const t  = j / (denseCnt - 1);
    const φ  = -turns * 2 * Math.PI * t;             // CW from +Z
    const latBase = -Math.PI / 2 + Math.PI * t;
    const lat     = latBase * (1 - bias);            // squeeze toward equator
    const y  = Math.sin(lat) * r;
    const ρ  = Math.cos(lat) * r;
    dense.push(new THREE.Vector3(Math.cos(φ) * ρ, y, Math.sin(φ) * ρ));
  }

  const cum: number[] = [0];
  let total = 0;
  for (let j = 1; j < denseCnt; j++) {
    total += dense[j].distanceTo(dense[j - 1]);
    cum.push(total);
  }

  const pts: THREE.Vector3[] = [];
  for (let i = 0; i < count; i++) {
    const target = (i * total) / (count - 1);
    let lo = 0, hi = cum.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      cum[mid] < target ? (lo = mid + 1) : (hi = mid);
    }
    const idx = lo;
    if (cum[idx] === target || idx === 0) pts.push(dense[idx].clone());
    else {
      const tSeg = (target - cum[idx - 1]) / (cum[idx] - cum[idx - 1]);
      pts.push(dense[idx - 1].clone().lerp(dense[idx], tSeg));
    }
  }
  return pts;
}
